import { useEffect, useRef, useState } from 'react';

import Input from '../../common/Input';
import Modal from '../../common/Modal';
import {
  DialogCard,
  DialogFooter,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from './folderDialogStyles';

import type { Folder } from '@src/types.ts/story';
import type { FC, KeyboardEvent } from 'react';

type Props = {
  folder: Folder;
  validate: (name: string) => string | null;
  onSubmit: (name: string) => Promise<void> | void;
  onClose: () => void;
};

const RenameFolderDialog: FC<Props> = ({
  folder,
  validate,
  onSubmit,
  onClose,
}) => {
  const [value, setValue] = useState(folder.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const submit = async () => {
    const trimmed = value.trim();
    if (trimmed === folder.name.trim()) {
      onClose();
      return;
    }
    const validationError = validate(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(trimmed);
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <Modal isOpen closeModal={onClose}>
      <DialogCard data-testid="rename-folder-dialog">
        <DialogTitle>Rename Folder</DialogTitle>
        <Input
          ref={inputRef}
          value={value}
          disabled={busy}
          fullWidth
          error={error ?? undefined}
          onChange={e => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKeyDown}
        />
        <DialogFooter>
          <SecondaryButton
            type="button"
            disabled={busy}
            onClick={onClose}
            data-testid="rename-folder-cancel"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            data-testid="rename-folder-confirm"
          >
            Rename
          </PrimaryButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

export default RenameFolderDialog;
